import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Mic, Video, Newspaper, Headphones, Users, ExternalLink } from "lucide-react";

const KIND_META = {
  podcast:    { label: "Podcast",     icon: Headphones,  color: "bg-fucsia text-white" },
  video:      { label: "Video",       icon: Video,       color: "bg-viola text-cream" },
  article:    { label: "Articolo",    icon: Newspaper,   color: "bg-giallo text-viola" },
  interview:  { label: "Intervista",  icon: Mic,         color: "bg-arancio text-viola" },
  conference: { label: "Conferenza",  icon: Users,       color: "bg-lilla text-viola" },
};

const FILTERS = [
  { key: "", label: "Tutto" },
  { key: "podcast", label: "Podcast" },
  { key: "video", label: "Video" },
  { key: "interview", label: "Interviste" },
  { key: "article", label: "Articoli" },
  { key: "conference", label: "Conferenze" },
];

// --- Embed helpers ---
function getYouTubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    const m = u.pathname.match(/\/(embed|shorts)\/([^/]+)/);
    if (m) return m[2];
  } catch { /* Invalid URL — return null for graceful fallback */ }
  return null;
}

function getSpotifyEmbed(url) {
  try {
    const u = new URL(url);
    if (!u.hostname.includes("spotify.com") && !u.hostname.includes("creators.spotify.com")) return null;
    // creators.spotify.com/pod/profile/<slug>/episodes/<id>  → convert to open.spotify.com/embed
    // Best effort: use spotify's oEmbed URL pattern via /embed prefix on open.spotify.com paths
    if (u.hostname.includes("open.spotify.com")) {
      // Strip query params (e.g. ?si=...) — they crash Spotify's embed player with a locale error
      return `https://open.spotify.com/embed${u.pathname}`;
    }
    // For anchor/creators links, fall back to null (Spotify blocks these in iframe reliably)
    return null;
  } catch {
    // Invalid URL — fallback to no embed
    return null;
  }
}

function MediaEmbed({ item }) {
  const yt = (item.kind === "video" || item.kind === "conference") ? getYouTubeId(item.url) : null;
  const sp = (item.kind === "podcast") ? getSpotifyEmbed(item.url) : null;

  if (yt) {
    return (
      <div className="aspect-video overflow-hidden rounded-xl bg-black" data-testid={`embed-youtube-${item.id}`}>
        <iframe
          src={`https://www.youtube.com/embed/${yt}`}
          title={item.title}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="h-full w-full border-0"
          loading="lazy"
        />
      </div>
    );
  }
  if (sp) {
    return (
      <div className="overflow-hidden rounded-xl" data-testid={`embed-spotify-${item.id}`}>
        <iframe
          src={sp}
          title={item.title}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
          allowFullScreen
          loading="lazy"
          className="w-full border-0"
          style={{ height: 232 }}
        />
      </div>
    );
  }
  return null;
}

function MediaCard({ item }) {
  const meta = KIND_META[item.kind] || KIND_META.article;
  const Icon = meta.icon;
  const hasEmbed = !item.embed_disabled && (getYouTubeId(item.url) || getSpotifyEmbed(item.url));
  const showBigCover = item.embed_disabled && item.cover_url;

  return (
    <article className="card-flat block" data-testid={`media-${item.id}`}>
      {hasEmbed && <div className="mb-4"><MediaEmbed item={item} /></div>}
      {showBigCover && (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="relative block mb-4 aspect-square overflow-hidden rounded-xl group"
          data-testid={`media-cover-${item.id}`}
        >
          <img src={item.cover_url} alt={item.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-viola/70 via-transparent to-transparent" />
          <div className="absolute inset-0 grid place-items-center">
            <div className="h-16 w-16 rounded-full bg-fucsia/90 grid place-items-center shadow-2xl transition-transform group-hover:scale-110">
              <Icon size={26} className="text-white" />
            </div>
          </div>
        </a>
      )}

      <div className="flex items-start gap-4">
        {!hasEmbed && !showBigCover && (
          item.cover_url ? (
            <img src={item.cover_url} alt={item.title} className="w-20 h-20 rounded-lg object-cover shrink-0" />
          ) : (
            <div className={`w-20 h-20 rounded-lg grid place-items-center shrink-0 ${meta.color}`}>
              <Icon size={28} />
            </div>
          )
        )}
        <div className="flex-1 min-w-0">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-widest ${meta.color}`}>
            <Icon size={10} /> {meta.label}
          </span>
          <h3 className="mt-2 font-display text-xl leading-tight">{item.title}</h3>
          {item.source && (
            <p className="mt-1 text-xs text-viola/60 font-semibold uppercase tracking-widest">
              {item.source}{item.date ? ` · ${item.date}` : ""}
            </p>
          )}
          {item.description && <p className="mt-2 text-sm text-viola/75 line-clamp-3">{item.description}</p>}
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-fucsia hover:underline"
            data-testid={`media-link-${item.id}`}
          >
            {hasEmbed ? "Apri nella piattaforma" : "Leggi / ascolta"} <ExternalLink size={10} />
          </a>
        </div>
      </div>
    </article>
  );
}

export default function Media() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = (kind) => {
    setLoading(true);
    const query = kind ? `?kind=${kind}` : "";
    http.get(`/media${query}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(filter); }, [filter]);

  return (
    <div className="container-editorial py-16 lg:py-24" data-testid="page-media">
      <div className="max-w-3xl">
        <span className="pill">Media</span>
        <h1 className="mt-4 font-display text-5xl sm:text-6xl tracking-tighter leading-[0.95]">Podcast, video, interviste, conferenze.</h1>
        <p className="mt-6 text-lg text-viola/75">Una raccolta delle mie presenze pubbliche: divulgazione radio/podcast, video, articoli firmati, conferenze e interviste.</p>
      </div>

      <div className="mt-10 flex flex-wrap gap-2" data-testid="media-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`media-filter-${f.key || "all"}`}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${filter === f.key ? "bg-viola text-cream" : "bg-lilla text-viola hover:bg-arancio/60"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-16 text-viola/60" data-testid="media-loading">Caricamento…</p>
      ) : items.length === 0 ? (
        <div className="mt-16 rounded-2xl bg-lilla p-12 text-center" data-testid="media-empty">
          <p className="font-display text-2xl">Presto tanti contenuti qui.</p>
          <p className="mt-3 text-viola/70">Podcast, interviste e video verranno aggiunti man mano.</p>
        </div>
      ) : (
        <div className="mt-12 grid md:grid-cols-2 gap-6" data-testid="media-grid">
          {items.map((m) => <MediaCard key={m.id} item={m} />)}
        </div>
      )}
    </div>
  );
}
